// Centralized export logic for Salary Breakdown Pro
import { showToast } from './stores.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const WATERMARK_CONFIG = {
  text: "Created with Salary Breakdown Pro",
  website: "https://www.appsphinx.com/developer/shree/salary-breakdown-pro ",
  separator: " | Visit "
};

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

export function exportAsCSV(calculation, totals, dueAmount, shouldHighlightDue) {
  const headers = ['Month/Year', 'Basic Pay (₹)', 'DA %', 'DA Amount (₹)', 'Due (₹)', 'Drawn (₹)', 'Arrears (₹)', 'Difference (₹)'];
  if (calculation.salaryType === 'pensioner') {
    headers.splice(6, 0, 'Commuted (₹)');
  }
  let csvContent = '\uFEFF' + headers.join(',') + '\n'; // UTF-8 BOM
  calculation.tableData.forEach(row => {
    const rowData = [
      row.monthYear,
      row.basicPay.toFixed(2),
      row.daRate,
      (row.daAmount || 0).toFixed(2),
      (row.salaryDue || 0).toFixed(2),
      (row.drawn || 0).toFixed(2),
      (row.commuted || 0).toFixed(2),
      (row.arrears || 0).toFixed(2),
      (row.difference || 0).toFixed(2)
    ];
    if (calculation.salaryType !== 'pensioner') {
      rowData.splice(6, 1); // Remove commuted column for employees
    }
    csvContent += rowData.join(',') + '\n';
  });
  // Add summary
  csvContent += '\nSummary\n';
  csvContent += `Total Due,${formatCurrency(totals.due)}\n`;
  csvContent += `Total Drawn (Drawn + Arrears),${formatCurrency(totals.drawn + totals.arrears)}\n`;
  csvContent += `Total Arrears,${formatCurrency(totals.arrears)}\n`;
  if (totals.commuted > 0) {
    csvContent += `Commuted Pension,${formatCurrency(totals.commuted)}\n`;
  }
  csvContent += `Total Difference,${formatCurrency(totals.difference)}\n`;
  if (shouldHighlightDue) {
    csvContent += `Amount to be Received,${formatCurrency(dueAmount)}\n`;
  }
  // Add watermark
  csvContent += `\n${WATERMARK_CONFIG.text}${WATERMARK_CONFIG.separator}${WATERMARK_CONFIG.website}\n`;
  downloadFile(csvContent, `${calculation.name}.csv`, 'text/csv');
}

export function exportAsTXT(calculation, totals, dueAmount, shouldHighlightDue) {
  let txtContent = `SALARY CALCULATION REPORT\n`;
  txtContent += `Calculation: ${calculation.name}\n`;
  txtContent += `Period: ${calculation.startDate} to ${calculation.endDate}\n`;
  txtContent += `Basic Pay: ${formatCurrency(calculation.basicPay)}\n`;
  txtContent += `Type: ${calculation.salaryType === 'employee' ? 'Employee' : 'Pensioner'}\n`;
  txtContent += `Generated: ${new Date().toLocaleString()}\n\n`;
  txtContent += `┌─────────────────┬──────────────┬────────┬──────────────┬──────────────┬──────────────┬──────────────┬──────────────┐\n`;
  txtContent += `│ Month/Year      │ Basic Pay    │ DA %   │ DA Amount    │ Due Amount   │ Drawn Amount │ Arrears      │ Difference   │\n`;
  if (calculation.salaryType === 'pensioner') {
    txtContent = txtContent.replace('│ Difference   │', '│ Commuted     │ Difference   │');
  }
  txtContent += `\n├─────────────────┼──────────────┼────────┼──────────────┼──────────────┼──────────────┼──────────────┼──────────────┤\n`;
  calculation.tableData.forEach(row => {
    const monthYear = row.monthYear.padEnd(15);
    const basicPay = formatCurrency(row.basicPay).padEnd(12);
    const daRate = `${row.daRate}%`.padEnd(6);
    const daAmount = formatCurrency(row.daAmount || 0).padEnd(12);
    const due = formatCurrency(row.salaryDue || 0).padEnd(12);
    const drawn = formatCurrency(row.drawn || 0).padEnd(12);
    const arrears = formatCurrency(row.arrears || 0).padEnd(12);
    if (calculation.salaryType === 'pensioner') {
      const commuted = formatCurrency(row.commuted || 0).padEnd(12);
      const difference = formatCurrency(row.difference || 0).padEnd(12);
      txtContent += `│ ${monthYear} │ ${basicPay} │ ${daRate} │ ${daAmount} │ ${due} │ ${drawn} │ ${commuted} │ ${difference} │\n`;
    } else {
      const difference = formatCurrency(row.difference || 0).padEnd(12);
      txtContent += `│ ${monthYear} │ ${basicPay} │ ${daRate} │ ${daAmount} │ ${due} │ ${drawn} │ ${arrears} │ ${difference} │\n`;
    }
  });
  txtContent += `└─────────────────┴──────────────┴────────┴──────────────┴──────────────┴──────────────┴──────────────┴──────────────┘\n\n`;
  // Summary section
  txtContent += `SUMMARY\n`;
  txtContent += `───────\n`;
  txtContent += `Total Due: ${formatCurrency(totals.due)}\n`;
  txtContent += `Total Drawn (Drawn + Arrears): ${formatCurrency(totals.drawn + totals.arrears)}\n`;
  txtContent += `Total Arrears: ${formatCurrency(totals.arrears)}\n`;
  if (totals.commuted > 0) {
    txtContent += `Commuted Pension: ${formatCurrency(totals.commuted)}\n`;
  }
  txtContent += `Total Difference: ${formatCurrency(totals.difference)}\n`;
  if (shouldHighlightDue) {
    txtContent += `\n⚠️  AMOUNT TO BE RECEIVED: ${formatCurrency(dueAmount)}\n`;
  }
  // Add watermark
  txtContent += `\n${WATERMARK_CONFIG.text}${WATERMARK_CONFIG.separator}${WATERMARK_CONFIG.website}\n`;
  downloadFile(txtContent, `${calculation.name}.txt`, 'text/plain');
}

export function exportAsPDF(calculation, totals, dueAmount, shouldHighlightDue, orientation = 'landscape') {
  const jsPdfOrientation = orientation === 'portrait' ? 'p' : 'l';
  const doc = new jsPDF(jsPdfOrientation, 'pt', 'a4');
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const margin = 40;
  let y = margin;

  // Helper function to check and add new page if needed
  function ensureSpace(requiredSpace) {
    if (y + requiredSpace > pageHeight - margin) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  }

  // Helper function to write text with overflow check
  function writeText(text, x, fontSize, isBold = false) {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    
    // Check if we need a new page
    ensureSpace(fontSize + 10);
    
    doc.text(text, x, y);
    y += fontSize + 10;
  }

  // Title and header information
  writeText(`Salary Calculation: ${calculation.name}`, margin, 16, true);
  writeText(`Period: ${calculation.startDate} to ${calculation.endDate}`, margin, 10);
  writeText(`Basic Pay: ${formatCurrency(calculation.basicPay).replace(/₹/g, 'INR')}`, margin, 10);
  writeText(`Type: ${calculation.salaryType === 'employee' ? 'Employee' : 'Pensioner'}`, margin, 10);
  writeText(`Generated: ${new Date().toLocaleString()}`, margin, 10);

  // Add some space before table
  y += 15;

  const commutedOpted = calculation.salaryType === 'pensioner' && calculation.commutation?.opted;
  const headers = [[
    'Month/Year',
    'Basic Pay (INR)',
    'DA %',
    'DA Amount (INR)',
    'Due (INR)',
    'Drawn (INR)',
    ...(commutedOpted ? ['Commuted (INR)'] : []),
    'Arrears (INR)',
    'Difference (INR)'
  ]];

  const data = calculation.tableData.map(row => [
    row.monthYear,
    row.basicPay.toFixed(2),
    row.daRate,
    (row.daAmount || 0).toFixed(2),
    (row.salaryDue || 0).toFixed(2),
    (row.drawn || 0).toFixed(2),
    ...(commutedOpted ? [(row.commuted || 0).toFixed(2)] : []),
    (row.arrears || 0).toFixed(2),
    (row.difference || 0).toFixed(2)
  ]);

  // Configure auto table with page break handling
  autoTable(doc, {
    head: headers,
    body: data,
    startY: y,
    styles: { 
      font: 'helvetica', 
      fontSize: 9, 
      cellPadding: 4,
      overflow: 'linebreak'
    },
    headStyles: { 
      fillColor: [34, 197, 94], 
      fontStyle: 'bold', 
      fontSize: 10,
      halign: 'right'
    },
    columnStyles: {
      0: { halign: 'left' },  // Month/Year
      1: { halign: 'right' }, // Basic Pay
      2: { halign: 'right' }, // DA %
      3: { halign: 'right' }, // DA Amount
      4: { halign: 'right' }, // Due
      5: { halign: 'right' }, // Drawn
      6: { halign: 'right' }, // Arrears
      7: { halign: 'right' }  // Difference
    },
    margin: { left: margin, right: margin },
    theme: 'grid',
    tableWidth: 'auto',
    didDrawPage: function(data) {
      y = data.cursor.y;
    }
  });

  // Add summary section
  y += 30; // Add space after table
  ensureSpace(150); // Check if we need a new page for summary

  // Summary section with proper spacing
  writeText('SUMMARY', margin, 12, true);
  writeText(`Total Due: ${formatCurrency(totals.due).replace(/₹/g, 'INR')}`, margin, 10);
  writeText(`Total Drawn (Drawn + Arrears): ${formatCurrency(totals.drawn + totals.arrears).replace(/₹/g, 'INR')}`, margin, 10);
  writeText(`Total Arrears: ${formatCurrency(totals.arrears).replace(/₹/g, 'INR')}`, margin, 10);
  
  if (totals.commuted > 0) {
    writeText(`Commuted Pension: ${formatCurrency(totals.commuted).replace(/₹/g, 'INR')}`, margin, 10);
  }
  
  writeText(`Total Difference: ${formatCurrency(totals.difference).replace(/₹/g, 'INR')}`, margin, 10);

  if (shouldHighlightDue) {
    doc.setTextColor(255, 0, 0); // Red color for amount to be received
    writeText(`AMOUNT TO BE RECEIVED: ${formatCurrency(dueAmount).replace(/₹/g, 'INR')}`, margin, 11, true);
    doc.setTextColor(0, 0, 0); // Reset text color
  }

  // Ensure space for watermark
  ensureSpace(40);
  
  // Add watermark at the bottom
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  const watermark = `${WATERMARK_CONFIG.text}${WATERMARK_CONFIG.separator}${WATERMARK_CONFIG.website}`;
  const textWidth = doc.getStringUnitWidth(watermark) * 9 / doc.internal.scaleFactor;
  const textX = (pageWidth - textWidth) / 2;
  doc.text(watermark, textX, y);

  doc.save(`${calculation.name}.pdf`);
  showToast('PDF downloaded successfully!', 'success');
}

export function exportAsJSON(calculation) {
  const json = JSON.stringify(calculation, null, 2);
  downloadFile(json, `${calculation.name}.json`, 'application/json');
}

export function copyJSONToClipboard(calculation) {
  const json = JSON.stringify(calculation, null, 2);
  navigator.clipboard.writeText(json).then(() => {
    showToast('Calculation JSON copied to clipboard!', 'success');
  }).catch(() => {
    showToast('Failed to copy JSON to clipboard', 'error');
  });
}

export function copyAsTXT(calculation, totals, dueAmount, shouldHighlightDue) {
  let txtContent = `SALARY CALCULATION REPORT\n`;
  txtContent += `Calculation: ${calculation.name}\n`;
  txtContent += `Period: ${calculation.startDate} to ${calculation.endDate}\n`;
  txtContent += `Basic Pay: ${formatCurrency(calculation.basicPay)}\n`;
  txtContent += `Type: ${calculation.salaryType === 'employee' ? 'Employee' : 'Pensioner'}\n`;
  txtContent += `Generated: ${new Date().toLocaleString()}\n\n`;
  txtContent += `┌─────────────────┬──────────────┬────────┬──────────────┬──────────────┬──────────────┬──────────────┬──────────────┐\n`;
  txtContent += `│ Month/Year      │ Basic Pay    │ DA %   │ DA Amount    │ Due Amount   │ Drawn Amount │ Arrears      │ Difference   │\n`;
  if (calculation.salaryType === 'pensioner') {
    txtContent = txtContent.replace('│ Difference   │', '│ Commuted     │ Difference   │');
  }
  txtContent += `\n├─────────────────┼──────────────┼────────┼──────────────┼──────────────┼──────────────┼──────────────┼──────────────┤\n`;
  calculation.tableData.forEach(row => {
    const monthYear = row.monthYear.padEnd(15);
    const basicPay = formatCurrency(row.basicPay).padEnd(12);
    const daRate = `${row.daRate}%`.padEnd(6);
    const daAmount = formatCurrency(row.daAmount || 0).padEnd(12);
    const due = formatCurrency(row.salaryDue || 0).padEnd(12);
    const drawn = formatCurrency(row.drawn || 0).padEnd(12);
    const arrears = formatCurrency(row.arrears || 0).padEnd(12);
    if (calculation.salaryType === 'pensioner') {
      const commuted = formatCurrency(row.commuted || 0).padEnd(12);
      const difference = formatCurrency(row.difference || 0).padEnd(12);
      txtContent += `│ ${monthYear} │ ${basicPay} │ ${daRate} │ ${daAmount} │ ${due} │ ${drawn} │ ${commuted} │ ${difference} │\n`;
    } else {
      const difference = formatCurrency(row.difference || 0).padEnd(12);
      txtContent += `│ ${monthYear} │ ${basicPay} │ ${daRate} │ ${daAmount} │ ${due} │ ${drawn} │ ${arrears} │ ${difference} │\n`;
    }
  });
  txtContent += `└─────────────────┴──────────────┴────────┴──────────────┴──────────────┴──────────────┴──────────────┴──────────────┘\n\n`;
  // Summary section
  txtContent += `SUMMARY\n`;
  txtContent += `───────\n`;
  txtContent += `Total Due: ${formatCurrency(totals.due)}\n`;
  txtContent += `Total Drawn (Drawn + Arrears): ${formatCurrency(totals.drawn + totals.arrears)}\n`;
  txtContent += `Total Arrears: ${formatCurrency(totals.arrears)}\n`;
  if (totals.commuted > 0) {
    txtContent += `Commuted Pension: ${formatCurrency(totals.commuted)}\n`;
  }
  txtContent += `Total Difference: ${formatCurrency(totals.difference)}\n`;
  if (shouldHighlightDue) {
    txtContent += `\n⚠️  AMOUNT TO BE RECEIVED: ${formatCurrency(dueAmount)}\n`;
  }
  // Add watermark
  txtContent += `\n${WATERMARK_CONFIG.text}${WATERMARK_CONFIG.separator}${WATERMARK_CONFIG.website}\n`;
  navigator.clipboard.writeText(txtContent).then(() => {
    showToast('Calculation copied to clipboard!', 'success');
  }).catch(() => {
    showToast('Failed to copy to clipboard', 'error');
  });
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast(`${filename} downloaded successfully!`, "success");
}

export { downloadFile, formatCurrency, WATERMARK_CONFIG }; 